import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useContext, JSX } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import moment from 'moment';
import usePostSetMyClientBankAccount from '../../../hooks/useApi/usePostSetMyClientBankAccount';
import { MultiStepFormProvider } from '../../../providers/MultiStepForm.provider';
import { usePendingData } from '../../../providers/PendingData.provider';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import AccountCreationForm from './AccountCreationForm';
import AccountCreationContext from './AccountCreationContext';
import usePostEmbedUrl from '../../../hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '../../../hooks/useApi/usePostSendPaylink';
import usePostAddInteracEmail from '../../../hooks/useApi/usePostAddInteracEmail';
import usePostIQ11Tokenize from '../../../hooks/useApi/usePostIQ11Tokenize';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import SendPaylinkContext, { SendPaylinkCtx } from './Dialogue/Paylink/PaylinkContext';
import { SendPaylinkDialogueHandler, AccountCreationDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import PaylinkDialogue from './Dialogue/Paylink/PaylinkDialogue';
import type { AccountsHandlers, NewAccountData } from '../types';
import PaylinkCreationPage from './PaylinkCreationPage';
import AccountCreationDialogueContext, { AccountCreationCtx } from './Dialogue/AccountCreation/AccountCreationContext';
import AccountCreationDialogue from './Dialogue/AccountCreation/AccountCreationDialogue';
import InteracInfoContext from './Interac/InteracInfoContext';

export type AccountCreationProps = {
  extraSteps?: {
    label: string;
    content: JSX.Element;
    showStep: () => boolean;
    hasError: () => boolean;
    percentageComplete: () => number;
  }[];
  isClientControlled?: boolean;
  customReview?: JSX.Element;
  isActive?: boolean;
  onStepChange: (idx: number) => void;
  onStepReject: (idx: number) => void;
  stepUser: number;
  stepUserChange: boolean;
  onComplete?: (data: NewAccountData) => void;
  onCompleteToken?: (token: string, paymentMethod: string) => void;
  onCompleteTokenSecondBank?: (token: string, paymentMethod: string) => void;
  onTeneativeSubmit?: () => void;
  isAllowMove: boolean;
  isAllowPaylink?: boolean;
  isAllow2Banks?: boolean;
  showSave?: boolean;
  defaultValues?: {
    currency: string;
    paymentMethod: string;
    ClientAccountsEnabled?: string;
    clientAccount?: string;
    country: string;
    account: string | undefined;
  };
  paylinkValues?: {
    PaymentType?: 'fund' | 'withdraw';
    Amount?: number;
    Currency?: string;
    DocumentID?: string;
    // new reoccuring feilds
    Frequency?: string;
    NameOfFrequency?: string;
    ScheduleStartDate?: string;
    ScheduleEndDate?: string;
    EndingAfterPayments?: string;
    SemiMonthlyFrequencyType?: string;
    Ending?: string;
    ReceiverName?: string;
    ReceiverEmailAddress?: string;
    ClientAccountID?: string;
    SendEmail?: boolean;
  };
};
type FC = FunctionComponent<AccountCreationProps>;

const AccountCreationContainer: FC = (props: AccountCreationProps) => {
  const history = useHistory();

  const {
    extraSteps,
    onComplete,
    isClientControlled = false,
    customReview,
    isActive = true,
    stepUser,
    stepUserChange,
    onTeneativeSubmit,
    onCompleteToken,
    onCompleteTokenSecondBank,
    onStepChange,
    onStepReject,
    isAllowMove,
    defaultValues,
    paylinkValues,
    showSave = false,
    isAllow2Banks = false,
    isAllowPaylink = true,
  } = props;
  const { onSubNavClick } = useNavMenu();
  const { reset, hasPendingData } = usePendingData();
  const { response } = useClientLogin();
  const [newAccountData, setNewAccountData] = useState<NewAccountData | null>(null);
  const [embedUrlResponse, postEmbedUrlResponse] = usePostEmbedUrl({});
  const [paylinkResponse, postSendPaylink] = usePostSendPaylink({});
  const [addInteracResponse, postAddInteracEmail] = usePostAddInteracEmail({});
  const [tokenizeResponse, postTokenize] = usePostIQ11Tokenize({});
  const [setDefaultBankAccountResponse, setDefaultBankAccount] = usePostSetMyClientBankAccount({});
  const [shouldSetDefaultAccount, setShouldSetDefaultAccount] = useState(false);
  const [view, viewState] = useState<'form' | 'paylink'>('form');
  const [shouldEmbedUrl, setShouldEmbedUrl] = useState(false);
  const [showSendPaylinkSuccessAlert, setShowSendPaylinkSuccessAlert] = useState(false);
  const [showSendPaylinkErrorAlert, setShowSendPaylinkErrorAlert] = useState(false);
  const [shouldSendPaylink, setShouldSendPaylink] = useState(false);
  const [sendPaylinkError, setSendPaylinkError] = useState('');
  const [payLink, setPayLink] = useState('');
  const [shouldTokenize, setShouldTokenize] = useState(false);
  const [shouldTokenizeSecondBank, setShouldTokenizeSecondBank] = useState(false);

  const [shouldAddEmail, setShouldAddEmail] = useState(false);
  const [showAccountCreationSuccessAlert, setShowAccountCreationSuccessAlert] = useState(false);
  const [showAccountCreationErrorAlert, setShowAccountCreationErrorAlert] = useState(false);
  const [accountCreationError, setAccountCreationError] = useState('');
  const InteracCtx = useContext(InteracInfoContext);
  const [addTransaction, setAddTransaction] = useState(false);

  const SendPaylink: SendPaylinkCtx = {
    showSendPaylinkSuccessAlert,
    showSendPaylinkErrorAlert,
    sendPaylinkError,
    payLink,
  };

  const AccountCreation: AccountCreationCtx = {
    showAccountCreationSuccessAlert,
    showAccountCreationErrorAlert,
    accountCreationError,
  };

  const setAccountDefault = (tokenAccount: string, clientAccountID: string) => {
    // set my bank account
    setDefaultBankAccount({
      ClientAccountID: clientAccountID,
      SetAsDefault: true,
      Token: tokenAccount,
      Purpose: 'operational',
      PortalUser: response.LoginUserName,
    });

    setShouldSetDefaultAccount(true);
  };
  useEffect(() => {
    if (shouldSetDefaultAccount) {
      const { Success } = setDefaultBankAccountResponse;
      if (Success === true) {
        if (onComplete && newAccountData) {
          onComplete(newAccountData);
          if (onCompleteToken) {
            onCompleteToken(newAccountData.Token, 'bank');
          }
        } else {
          setShowAccountCreationSuccessAlert(true);
        }
      }
      setShouldSetDefaultAccount(false);
    }
  }, [setDefaultBankAccountResponse]);

  useEffect(() => {
    if (shouldAddEmail) {
      if (addInteracResponse.Success || addTransaction) {
        if (onComplete && newAccountData) {
          onComplete(newAccountData);
        } else {
          setShowAccountCreationSuccessAlert(true);
        }
      } else {
        setShowAccountCreationErrorAlert(true);
        setAccountCreationError(addInteracResponse.ErrorMessage);
      }
      setShouldAddEmail(false);
    }
  }, [addInteracResponse, addTransaction]);
  useEffect(() => {
    if (shouldTokenize) {
      if (tokenizeResponse.Success) {
        if (!isAllow2Banks || !newAccountData?.saveAccountInformationSecond) {
          if (defaultValues?.ClientAccountsEnabled) {
            setAccountDefault(tokenizeResponse.Token, defaultValues?.clientAccount || '');
          } else {
            if (onComplete && newAccountData) {
              onComplete(newAccountData);
              if (onCompleteToken) {
                onCompleteToken(newAccountData.Token, 'bank');
              }
            } else {
              setShowAccountCreationSuccessAlert(true);
            }
          }
        } else if (isAllow2Banks && newAccountData?.saveAccountInformationSecond) {
          if (newAccountData.saveAccountInformationSecond) {
            setShouldTokenizeSecondBank(true);
            postTokenize({
              PortalUser: response.LoginUserName,
              AccountHolderName:
                newAccountData.companyNameSecond && newAccountData.companyNameSecond !== ''
                  ? newAccountData.companyNameSecond
                  : `${newAccountData.firstNameSecond || ''} ${newAccountData.lastNameSecond || ''}`,
              AccountNumber: newAccountData.AccountNumberSecond,
              FinancialInstitutionNumber: newAccountData.FinancialInstitutionNumberSecond,
              BranchTransitNumber: newAccountData.BranchTransitNumberSecond,
              ABARoutingNumber: newAccountData.ABARoutingNumberSecond,
              ClientReferenceNumber: newAccountData.clientReferenceNumberSecond,
              ClientControlled: isClientControlled,
            });
          }
        }
      } else {
        setShowAccountCreationErrorAlert(true);
        setAccountCreationError(tokenizeResponse.ErrorMessage);
      }
      setShouldTokenize(false);
    }

    if (shouldTokenizeSecondBank) {
      if (tokenizeResponse.Success) {
        if (onComplete && newAccountData) {
          onComplete(newAccountData);
        } else {
          setShowAccountCreationSuccessAlert(true);
        }
      } else {
        setShowAccountCreationErrorAlert(true);
        setAccountCreationError(tokenizeResponse.ErrorMessage);
      }
      setShouldTokenizeSecondBank(false);
    }
  }, [tokenizeResponse]);
  const dialogueHandlers = {
    handlePaylinkDialogue: (handlerProps: DialogueHandlerProps<SendPaylinkDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers2: SendPaylinkDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
            },
          },
          view: {
            default: () => {
              setShowSendPaylinkSuccessAlert(false);
              onSubNavClick('paylinkHistory', '/elinx-history', 'eLinx History');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers2[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    handleAccountCreationDialogue: (handlerProps: DialogueHandlerProps<AccountCreationDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers2: AccountCreationDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowAccountCreationErrorAlert(false);
            },
          },
          view: {
            default: () => {
              setShowAccountCreationErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowAccountCreationSuccessAlert(false);
              if (isClientControlled) {
                history.push('/operational-accounts');
              } else {
                history.push('/contacts');
              }
            },
          },
          view: {
            default: () => {
              setShowAccountCreationSuccessAlert(false);
              if (isClientControlled) {
                history.push('/operational-accounts');
              } else {
                history.push('/contacts');
              }
            },
          },
        },
      };
      const dialogueState = dialogueHandlers2[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  const handler: AccountsHandlers = {
    onAccountCreation: (_newAccountData: NewAccountData) => {
      if (isActive) {
        if (
          (_newAccountData.paymentMethod === 'creditCard' || _newAccountData.paymentMethod === 'bank') &&
          _newAccountData.connectionType === 'Paylink'
        ) {
          postEmbedUrlResponse(buildEmbedUrlParams(_newAccountData));
          setShouldEmbedUrl(true);
          setNewAccountData(_newAccountData);
          reset();
        } else if (_newAccountData.paymentMethod === 'e-transfer') {
          if (
            ((!vp.toBool(defaultValues?.ClientAccountsEnabled) &&
              (defaultValues?.account === 'new' || (extraSteps || []).length === 0)) ||
              InteracCtx.addInteracEmail) &&
            _newAccountData.saveAccountInformation
          ) {
            setShouldAddEmail(true);
            postAddInteracEmail({
              PortalUser: response.LoginUserName,
              RecipientName: _newAccountData.RecipientName,
              RecipientPhoneNumber:
                _newAccountData.etransferConnectionType === 'phone' ? _newAccountData.RecipientPhoneNumber : '',
              RecipientEmailAddress:
                _newAccountData.etransferConnectionType === 'email' ? _newAccountData.RecipientEmailAddress : '',
              ClientReferenceNumber: _newAccountData.clientReferenceNumber,
              Question: _newAccountData.Question,
              Answer: _newAccountData.Answer,
              ClientControlled: isClientControlled,
              Message: _newAccountData.Message,
              ClientAccountID: defaultValues?.clientAccount || '',
            });
          } else if (onComplete && _newAccountData) {
            onComplete(_newAccountData);
          }
          setNewAccountData(_newAccountData);
          setAddTransaction(true);
          reset();
        } else if (_newAccountData.paymentMethod === 'creditCard') {
          if (onComplete) {
            onComplete(_newAccountData);
          } else {
            setShowAccountCreationSuccessAlert(true);
          }
          reset();
        } else if (_newAccountData.paymentMethod === 'bank' && isClientControlled) {
          if (onComplete) {
            onComplete(_newAccountData);
          } else {
            setShowAccountCreationSuccessAlert(true);
          }
          reset();
        } else if (_newAccountData.paymentMethod === 'bank' && !isClientControlled) {
          if (_newAccountData.saveAccountInformation) {
            setShouldTokenize(true);
            postTokenize({
              PortalUser: response.LoginUserName,
              AccountHolderName:
                _newAccountData.companyName && _newAccountData.companyName !== ''
                  ? _newAccountData.companyName
                  : `${_newAccountData.firstName || ''} ${_newAccountData.lastName || ''}`,
              AccountNumber: _newAccountData.AccountNumber,
              FinancialInstitutionNumber: _newAccountData.FinancialInstitutionNumber,
              BranchTransitNumber: _newAccountData.BranchTransitNumber,
              ABARoutingNumber: _newAccountData.ABARoutingNumber,
              ClientReferenceNumber: _newAccountData.clientReferenceNumber,
              ClientControlled: isClientControlled,
              ClientAccountID: defaultValues?.clientAccount || '',
              Address1: _newAccountData.address1 || '',
              City: _newAccountData?.city || '',
              Province: _newAccountData.province || '',
              PostalCode: _newAccountData.postalCode || '',
              PhoneNumber: _newAccountData.contactNumber || '',
              Country: _newAccountData.country || '',
            });
          } else if (isAllow2Banks && !_newAccountData.saveAccountInformation) {
            if (_newAccountData.saveAccountInformationSecond) {
              setShouldTokenizeSecondBank(true);
              postTokenize({
                PortalUser: response.LoginUserName,
                AccountHolderName:
                  _newAccountData.companyNameSecond && _newAccountData.companyNameSecond !== ''
                    ? _newAccountData.companyNameSecond
                    : `${_newAccountData.firstNameSecond || ''} ${_newAccountData.lastNameSecond || ''}`,
                AccountNumber: _newAccountData.AccountNumberSecond,
                FinancialInstitutionNumber: _newAccountData.FinancialInstitutionNumberSecond,
                BranchTransitNumber: _newAccountData.BranchTransitNumberSecond,
                ABARoutingNumber: _newAccountData.ABARoutingNumberSecond,
                ClientReferenceNumber: _newAccountData.clientReferenceNumberSecond,
                ClientControlled: isClientControlled,
              });
            }
          } else if (onComplete && _newAccountData) {
            onComplete(_newAccountData);
          }
          setNewAccountData(_newAccountData);
          reset();
        }
      }
    },
  };

  useEffect(() => {
    if (embedUrlResponse.Success && shouldEmbedUrl) {
      setShouldSendPaylink(true);
      const now = moment().format('YYYY-MM-DD');
      const isToday =
        moment(now).format('YYYY-MM-DD') === moment(Number(paylinkValues?.ScheduleStartDate)).format('YYYY-MM-DD') ||
        paylinkValues?.ScheduleStartDate === undefined ||
        paylinkValues?.ScheduleStartDate === '';
      postSendPaylink({
        SenderName: newAccountData?.senderName || '',
        ReceiverName: newAccountData?.companyName
          ? `${newAccountData?.companyName || ''}`
          : newAccountData?.RecipientName
            ? `${newAccountData?.RecipientName || ''}`
            : `${newAccountData?.firstName || ''} ${newAccountData?.lastName || ''}`,
        ReceiverEmailAddress: newAccountData?.paylinkEmailAddress || newAccountData?.emailAddress || '',
        IframeKey: embedUrlResponse.IframeKey || '',
        InvoiceNumber: newAccountData?.invoiceNumber || '',
        ClientReferenceNumber: newAccountData?.clientReferenceNumber || '',
        Note: newAccountData?.note || '',
        RecipientMessage: newAccountData?.note || '',
        PaymentType: paylinkValues?.PaymentType,
        Amount: paylinkValues?.Amount,
        Currency: paylinkValues?.Currency,
        DocumentID: paylinkValues?.DocumentID,
        Frequency: !isToday ? paylinkValues?.Frequency : '',
        NameOfFrequency: !isToday ? paylinkValues?.NameOfFrequency : '',
        ScheduleStartDate: !isToday ? moment(Number(paylinkValues?.ScheduleStartDate)).format('YYYY-MM-DD') : '',
        ScheduleEndDate:
          !isToday && paylinkValues?.Ending === 'End Date'
            ? moment(Number(paylinkValues?.ScheduleEndDate)).format('YYYY-MM-DD')
            : '',
        EndingAfterPayments: !isToday && paylinkValues?.Ending === 'Payments' ? paylinkValues?.EndingAfterPayments : '',
        SemiMonthlyFrequencyType: !isToday ? paylinkValues?.SemiMonthlyFrequencyType : '',
        ClientAccountID: paylinkValues?.ClientAccountID,
        SendEmail: newAccountData?.SendEmail || false,
      });
      setShouldEmbedUrl(false);
    }
  }, [embedUrlResponse.Success, shouldEmbedUrl]);

  useEffect(() => {
    if (shouldSendPaylink === true && paylinkResponse) {
      const { Success, ErrorMessage } = paylinkResponse;

      if (Success === true) {
        setPayLink(paylinkResponse.Link);
        // setShowSendPaylinkSuccessAlert(true);
        // show review page
        viewState('paylink');
      } else if (Success === false) {
        setShowSendPaylinkErrorAlert(true);
        setSendPaylinkError(ErrorMessage);
      }
      setShouldSendPaylink(false);
    }
  }, [paylinkResponse]);

  return (
    <div>
      <SendPaylinkContext.Provider value={SendPaylink}>
        <AccountCreationDialogueContext.Provider value={AccountCreation}>
          <MultiStepFormProvider
            context={AccountCreationContext}
            disablePendingData
            onSubmit={handler.onAccountCreation}
          >
            <>
              {view === 'form' && (
                <AccountCreationForm
                  extraSteps={extraSteps || []}
                  customReview={customReview}
                  isActive={isActive}
                  onStepChange={onStepChange}
                  onStepReject={onStepReject}
                  onTeneativeSubmit={onTeneativeSubmit}
                  stepUser={stepUser}
                  title={isClientControlled ? 'Add Account' : 'Add New Contact'}
                  isAllowPaylink={isAllowPaylink}
                  showSave={showSave}
                  stepUserChange={stepUserChange}
                  isAllowMove={isAllowMove}
                  isAllow2Banks={isAllow2Banks}
                  defaultValues={defaultValues}
                  isClientControlled={isClientControlled}
                  onCompleteToken={onCompleteToken}
                  onCompleteTokenSecondBank={onCompleteTokenSecondBank}
                />
              )}
              {view === 'paylink' && paylinkResponse && <PaylinkCreationPage eLinx={paylinkResponse} />}
            </>
          </MultiStepFormProvider>
          <Prompt when={hasPendingData} message="You have unsaved account data that will be discarded." />
          <PaylinkDialogue handleDialogue={dialogueHandlers.handlePaylinkDialogue} />
          <AccountCreationDialogue handleDialogue={dialogueHandlers.handleAccountCreationDialogue} />
        </AccountCreationDialogueContext.Provider>
      </SendPaylinkContext.Provider>
    </div>
  );

  function buildEmbedUrlParams(accountData: NewAccountData): vp.EmbedUrlRequest {
    const embedUrlData: vp.EmbedUrlRequest = {
      RedirectURL: 'https://request-dev.vopay.com/',
      Language: accountData.language,
      RedirectMethod: 'JavascriptMessage',
      PaymentSelectionMethod: accountData.paymentMethod === 'creditCard' ? 'credit' : 'bank',
      AccountSelectionMethod: accountData.accountSelectionMethod,
      ClientControlled: isClientControlled,
      ClientReferenceNumber: accountData.clientReferenceNumber,
      TermsAndConditions: accountData.termsAndConditions,
      Country: accountData.country,
      ClientAccountID: paylinkValues?.ClientAccountID,
    };
    return embedUrlData;
  }
};

export default AccountCreationContainer;
